const PROBE_ORIGIN = "https://app.invalid";

/** Only same-origin app paths are valid post-login return targets. */
export function safeFrom(from: string | undefined): string | undefined {
  if (!from?.startsWith("/") || from.startsWith("//")) return undefined;
  // Browsers treat "\" like "/" ("/\evil.example" is protocol-relative), and
  // control characters are stripped before parsing; reject both outright.
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
  if (/[\\\u0000-\u001f\u007f]/.test(from)) return undefined;
  let url: URL;
  try {
    url = new URL(from, PROBE_ORIGIN);
  } catch {
    return undefined;
  }
  if (url.origin !== PROBE_ORIGIN) return undefined;
  if (url.pathname === "/login" || url.pathname.startsWith("/login/")) return undefined;
  const result = `${url.pathname}${url.search}${url.hash}`;
  // Dot segments normalize to a protocol-relative path ("/.//evil.example"
  // → "//evil.example"), which a browser would load from another origin:
  // the result itself must be a single-slash path that stays on the origin.
  if (result.startsWith("//")) return undefined;
  try {
    if (new URL(result, PROBE_ORIGIN).origin !== PROBE_ORIGIN) return undefined;
  } catch {
    return undefined;
  }
  return result;
}

/** Whose session ended, so its return link isn't followed by someone else. */
export const ENDED_FOR_KEY = "nextsub.ended_for";

export function rememberEndedSession(userId: string | null) {
  try {
    if (userId) sessionStorage.setItem(ENDED_FOR_KEY, userId);
  } catch {
    /* storage unavailable */
  }
}

export function forgetEndedSession() {
  try {
    sessionStorage.removeItem(ENDED_FOR_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * May the user who just signed in follow a return link? Not when it was left
 * by another user's ended session (a shared machine). The record is used once.
 */
export function mayFollowReturnLink(signedIn: string | null, { ended = false } = {}): boolean {
  try {
    const endedFor = sessionStorage.getItem(ENDED_FOR_KEY);
    sessionStorage.removeItem(ENDED_FOR_KEY);
    // A link from an ended session whose owner wasn't recorded isn't followed.
    if (!endedFor) return !ended;
    return endedFor === signedIn;
  } catch {
    return !ended;
  }
}
